import { format, isBefore, isToday, parseISO, startOfDay } from "date-fns";

export const formatTaskDate = (dateString: string) => {
  try {
    const date = parseISO(dateString);

    if (isToday(date)) {
      return "Today";
    }

    return format(date, "MM/dd");
  } catch {
    return "";
  }
};

export const formatTaskTime = (dateString: string) => {
  try {
    const date = parseISO(dateString);
    return format(date, "hh:mm a");
  } catch {
    return "";
  }
};

export const toDateISOString = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, "0");

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());

  return `${year}-${month}-${day}T12:00:00.000Z`;
};

/**
 * Read a calendar-block timestamp back as the calendar day it stands for.
 *
 * The API stores a block's start/end as a UTC instant whose time-of-day is a
 * marker, not a real check-in time: 12:30/11:30 for blocks written by this app
 * and 00:00 for blocks imported from the PMS. Parsing the midnight form with
 * `new Date()` lands on the previous day in every negative UTC offset, so an
 * Eastern user opening a synced block saw both dates a day early, and saving
 * pushed that shift back to the PMS (MSMS-55).
 *
 * Taking the UTC calendar day and rebuilding it at local midnight makes the
 * read the exact inverse of `toDateISOString`, so a round-trip through a form
 * cannot move the block.
 *
 * @param value - ISO timestamp (or Date) as returned by the API
 * @returns A local-midnight Date on the block's intended calendar day
 */
export const fromDateISOString = (value: string | Date): Date => {
  const parsed = value instanceof Date ? value : new Date(value);

  return new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate()
  );
};

export const isBeforeToday = (date: Date): boolean => {
  return isBefore(startOfDay(date), startOfDay(new Date()));
};

export const formatTimeOnly = (hour: number, minute: number): string => {
  // 0 and 12 both display as 12 on a 12-hour clock.
  const displayHour = hour % 12 || 12;
  const amPm = hour >= 12 ? "PM" : "AM";
  return `${displayHour.toString().padStart(2, "0")}:${minute
    .toString()
    .padStart(2, "0")} ${amPm}`;
};

export const formatReportDate = (dateString: string): string => {
  try {
    const date = parseISO(dateString);
    return format(date, "dd/MM/yyyy HH:mm");
  } catch {
    return "";
  }
};
